import { IconsAlignEnum } from '@vedalib/editor/lib/brand'
import type { TestGroupingGroup, TestGroupingItem } from '@vedalib/editor/lib/elements'
import { generateId } from '@vedalib/editor/lib/utils/id'
import { rtValueToText, textToRtValue } from '@vedalib/rich-text'
import type { CSSProperties } from 'react'

import { t } from 'services/Translation'

import type { ValidationMap } from './types'

export const getGroupName = (group: TestGroupingGroup): string =>
  rtValueToText(group.target.label) || group.target.image?.accessibility || group.value

export const STACK_DRAGGABLE_PREFIX = 'stack:'

export const createStackDraggableId = (groupValue: string, itemValue: string) =>
  `${STACK_DRAGGABLE_PREFIX}${groupValue}:${itemValue}`

export const parseStackDraggableId = (draggableId: string) => {
  const isFromStack = draggableId.startsWith(STACK_DRAGGABLE_PREFIX)
  const [, sourceGroupValue, itemValue] = draggableId.split(':')
  return { isFromStack, sourceGroupValue, itemValue }
}

type GetCardsDataType = {
  validationMap: ValidationMap
  source: TestGroupingItem[]
}

export const getAllPaddings = (padding: string | number): [number, number, number, number] => {
  const pad = String(padding)
    .split(' ')
    .map((el) => parseInt(el) || 0)

  switch (pad.length) {
    case 1:
      return [pad[0], pad[0], pad[0], pad[0]]
    case 2:
      return [pad[0], pad[1], pad[0], pad[1]]
    case 3:
      return [pad[0], pad[1], pad[2], pad[1]]
    default:
      return [pad[0], pad[1], pad[2], pad[3]]
  }
}

export const getCardsData = (items: TestGroupingGroup[]) => {
  const dest = items.map(({ value, target }) => ({
    target,
    value,
    items: [] as TestGroupingItem[],
  }))
  const { source, validationMap } = items.reduce(
    (result, el) => {
      ;(el.items || []).forEach((e) => {
        if (e) {
          result.validationMap[e.value] = el.value
          result.source.push(e)
        }
      })

      return result
    },
    {
      validationMap: {},
      source: [],
    } as GetCardsDataType,
  )

  return { dest, source, validationMap }
}

export const borderRadiusContent = (styles: CSSProperties) => {
  const borderRadius = parseInt(String(styles.borderRadius))
  const borderWidth = parseInt(String(styles.borderWidth))
  return borderRadius > borderWidth ? borderRadius - borderWidth : 0
}

export const createNewAnswer = ({ index }: { index: number }): TestGroupingItem => ({
  value: generateId(),
  label: textToRtValue(`${t('elements.testGrouping.answer')} ${index}`),
  overlay: '',
  image: null,
})

export const createNewGroup = ({ index }: { index: number }): TestGroupingGroup => ({
  value: generateId(),
  target: {
    label: textToRtValue(`${t('elements.testGrouping.group')} ${index}`),
    overlay: '',
    image: null,
  },
  items: [],
})

export const createDuplicatedGroup = (group: TestGroupingGroup): TestGroupingGroup => ({
  ...group,
  value: generateId(),
  items: (group.items || []).map((item) => ({
    ...item,
    value: generateId(),
  })),
})

export const getNavPositionStyle = (
  iconsAlign: string | undefined,
  padding: number | string | undefined,
) => {
  const paddings = getAllPaddings(String(padding || 0))
  const [top, right, bottom, left] = paddings

  switch (iconsAlign) {
    case IconsAlignEnum.leftUp:
      return { top, left }
    case IconsAlignEnum.rightUp:
      return { top, right }
    case IconsAlignEnum.leftDown:
      return { bottom, left }
    case IconsAlignEnum.rightDown:
      return { right, bottom }
    case IconsAlignEnum.hide:
      return { display: 'none' as const }
    default:
      return { right, bottom }
  }
}
